// src/pages/ExerciseView.jsx
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

function useQuery() {
  const { search } = useLocation();
  return new URLSearchParams(search);
}

function fmtDate(iso) {
  try {
    const d = new Date(iso);
    if (!isNaN(d)) {
      return d.toLocaleString("it-IT", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    }
  } catch {}
  return iso;
}

export default function ExerciseView() {
  const q = useQuery();
  const key = q.get("key") || "";
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [doc, setDoc] = useState(null); // JSON completo
  const [core, setCore] = useState(null); // parte significativa per l'utente

  useEffect(() => {
    (async () => {
      setLoading(true);
      setErr("");
      try {
        if (!key) {
          setErr("Nessun file specificato.");
          return;
        }

        // ✅ FIX MINIMO: usare l'endpoint corretto /api/media?key=...
        const url =
          "/api/media?key=" + encodeURIComponent(key) + "&ts=" + Date.now();

        const res = await fetch(url);
        if (!res.ok) {
          setErr(`Impossibile aprire il file (HTTP ${res.status}).`);
          return;
        }
        const j = await res.json();
        setDoc(j);

        // Estrai solo le info utili
        const data = j?.data || {};
        const out = {
          lesson_title:
            data.lesson_title ||
            (j?.lesson_id ? String(j.lesson_id).replace(/^lesson_/, "") : ""),
          completion_date: data.completion_date || j?.now || "",
          exercise_type: data.exercise_type || "",
          instructions: data.exercise_instructions || "",
          answer:
            data.answer_data ??
            data.answer_text ??
            data.answer ??
            "", // compatibilità campi
          word_count:
            typeof data.word_count === "number" ? data.word_count : undefined,
          time_sec:
            typeof data.completion_time_seconds === "number"
              ? data.completion_time_seconds
              : undefined,
        };
        setCore(out);
      } catch (e) {
        setErr(String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [key]);

  if (loading) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <h1 className="text-2xl font-bold mb-4">Esercizio</h1>
        <p>Caricamento…</p>
      </div>
    );
  }

  if (err) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <h1 className="text-2xl font-bold mb-4">Esercizio</h1>
        <p className="text-red-600">{err}</p>
        <a
          href="/exercise-history"
          className="text-blue-600 underline mt-4 block"
        >
          ← Torna a Rileggi Esercizi
        </a>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="flex items-start justify-between gap-4">
        <h1 className="text-2xl font-bold">
          {core?.lesson_title || "Esercizio"}
        </h1>
        <a
          href="/exercise-history"
          className="text-blue-600 underline shrink-0"
        >
          ← Indietro
        </a>
      </div>

      <div className="mt-4 grid gap-4">
        {core?.completion_date && (
          <div className="text-sm text-gray-600">
            Completato il:{" "}
            <span className="font-medium">{fmtDate(core.completion_date)}</span>
          </div>
        )}

        {(core?.time_sec !== undefined || core?.word_count !== undefined) && (
          <div className="text-sm text-gray-600 space-x-4">
            {core.time_sec !== undefined && (
              <span>
                Tempo impiegato: <b>{core.time_sec}s</b>
              </span>
            )}
            {core.word_count !== undefined && (
              <span>
                Parole: <b>{core.word_count}</b>
              </span>
            )}
          </div>
        )}

        {core?.instructions && (
          <div className="rounded-xl border bg-white shadow-sm p-4">
            <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
              Istruzioni
            </div>
            <div className="whitespace-pre-wrap">{core.instructions}</div>
          </div>
        )}

        <div className="rounded-xl border bg-white shadow-sm p-4">
          <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
            La tua risposta
          </div>
          <div className="whitespace-pre-wrap break-words">
            {typeof core?.answer === "string" ? (
              core.answer
            ) : (
              <pre className="text-sm">{JSON.stringify(core?.answer, null, 2)}</pre>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
